import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../services/firebase";
import { FormLabel } from "../../components/form-label/FormLabel";
import { CollectedData } from "../../types";

export default function CollectedDataDetail() {
  const { recordId } = useParams<{ recordId: string }>();
  const [searchParams] = useSearchParams();
  const tenantIdParam = searchParams.get("tenantId") ?? "";
  const navigate = useNavigate();

  const [record, setRecord] = useState<CollectedData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!recordId) return;
    const fetch = async () => {
      setLoading(true);
      setError(null);
      try {
        const snap = await getDoc(
          doc(db, "collected_data", decodeURIComponent(recordId))
        );
        if (snap.exists()) {
          setRecord(snap.data() as CollectedData);
        } else {
          setError("Record non trovato");
        }
      } catch (e) {
        console.error(e);
        setError("Errore nel caricamento del record");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [recordId]);

  const formatDate = (ts: { seconds: number } | null) =>
    ts ? new Date(ts.seconds * 1000).toLocaleString("it-IT") : "—";

  const backUrl = tenantIdParam
    ? `/chatbot/monitoring/collected?tenantId=${tenantIdParam}`
    : "/chatbot/monitoring/collected";

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="container">
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
        <FormLabel label="Dettaglio dati raccolti" />
        <Button variant="outlined" onClick={() => navigate(backUrl)}>
          Indietro
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {record && (
        <>
          {/* Metadata */}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Informazioni
            </Typography>
            <Box className="flex-col gap-2" sx={{ fontSize: "0.875rem" }}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                  Utente (telefono)
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {record.user_phone}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                  Flow ID
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {record.flow_id}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
                  Completato il
                </Typography>
                <Typography variant="body2">{formatDate(record.completed_at)}</Typography>
              </Box>
            </Box>
          </Paper>

          {/* Collected fields */}
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Dati raccolti ({Object.keys(record.data).length} campi)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Campo</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Valore</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Object.entries(record.data).map(([key, value]) => (
                    <TableRow key={key} hover>
                      <TableCell sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                        {key}
                      </TableCell>
                      <TableCell>
                        {typeof value === "object"
                          ? JSON.stringify(value)
                          : String(value)}
                      </TableCell>
                    </TableRow>
                  ))}
                  {Object.keys(record.data).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} align="center">
                        Nessun dato
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </div>
  );
}
